import React, { useEffect, useState } from "react";
import { Shield } from "lucide-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Save, Loader2 } from "lucide-react";
import {
  PageContainer,
  PageHeader,
  Card,
  SectionTitle,
} from "@/components/layout/PageShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { useAuth } from "@/contexts/AuthContext";
import { useLocale } from "@/contexts/LocaleProvider";
import api from "@/lib/api";
import { toast } from "sonner";

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-[12px] text-slate-600 mb-1 block">{label}</Label>
      {children}
    </div>
  );
}

function Toggle({ label, hint, checked, onCheckedChange }) {
  return (
    <div className="flex items-start justify-between gap-3 py-2 border-b last:border-b-0">
      <div>
        <div className="text-[12.5px] font-medium text-slate-800">{label}</div>
        {hint && <div className="text-[11px] text-slate-500 mt-0.5">{hint}</div>}
      </div>
      <Switch checked={checked} onCheckedChange={onCheckedChange} />
    </div>
  );
}

const CURRENCY_LABELS = {
  AED: "AED — UAE Dirham",
  USD: "USD — US Dollar",
  EUR: "EUR — Euro",
  SAR: "SAR — Saudi Riyal",
  QAR: "QAR — Qatari Riyal",
  OMR: "OMR — Omani Rial",
  BHD: "BHD — Bahraini Dinar",
  KWD: "KWD — Kuwaiti Dinar",
};

export default function GeneralSettings() {
  const { user } = useAuth();
  const { setLocale } = useLocale();
  const queryClient = useQueryClient();
  const branchId = user?.default_branch?.id;
  const currency = user?.company_currency || user?.company?.currency || user?.default_branch?.currency || "AED";
  const [form, setForm] = useState(null);
  const [twoFaSetup, setTwoFaSetup] = useState(null);
  const [twoFaCode, setTwoFaCode] = useState("");

  const { data: twoFaStatus } = useQuery({
    queryKey: ["user-2fa"],
    queryFn: async () => {
      const { data: res } = await api.get("/user/2fa");
      return res;
    },
  });

  const { data, isLoading } = useQuery({
    queryKey: ["pos-settings", branchId],
    queryFn: async () => {
      const { data: res } = await api.get("/pos-settings", { params: { branch_id: branchId } });
      return res.settings;
    },
    enabled: Boolean(branchId),
  });

  useEffect(() => {
    if (data) setForm(data);
  }, [data]);

  const saveMutation = useMutation({
    mutationFn: async () => api.put("/pos-settings", { settings: form }, { params: { branch_id: branchId } }),
    onSuccess: async () => {
      queryClient.invalidateQueries({ queryKey: ["pos-settings", branchId] });
      if (form?.language === "en" || form?.language === "ar") {
        await setLocale(form.language);
      }
      toast.success("General settings saved");
    },
    onError: () => toast.error("Failed to save settings"),
  });

  function setField(key, value) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  if (isLoading || !form) {
    return (
      <PageContainer>
        <div className="h-40 grid place-items-center text-slate-500">
          <Loader2 className="h-6 w-6 animate-spin" />
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageHeader
        title="General Settings"
        subtitle={`Regional, security & POS preferences — ${user?.default_branch?.name || "branch"}`}
        actions={
          <Button
            size="sm"
            className="h-9 bg-[#16A34A] hover:bg-emerald-700"
            data-testid="general-save"
            onClick={() => saveMutation.mutate()}
            disabled={saveMutation.isPending}
          >
            {saveMutation.isPending ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <Save size={14} className="mr-1.5" />}
            Save Changes
          </Button>
        }
      />

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <Card className="p-5">
          <SectionTitle>Regional</SectionTitle>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-5 gap-y-3">
            <Field label="Language">
              <Select
                value={form.language}
                onValueChange={async (v) => {
                  setField("language", v);
                  if (v === "en" || v === "ar") await setLocale(v);
                }}
              >
                <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="en">English</SelectItem>
                  <SelectItem value="ar">العربية (Arabic)</SelectItem>
                  <SelectItem value="ur">اردو (Urdu)</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Time Zone">
              <Select value={form.timezone} onValueChange={(v) => setField("timezone", v)}>
                <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="dubai">Asia / Dubai (GMT+4)</SelectItem>
                  <SelectItem value="riyadh">Asia / Riyadh (GMT+3)</SelectItem>
                  <SelectItem value="muscat">Asia / Muscat (GMT+4)</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Date Format">
              <Select value={form.date_format} onValueChange={(v) => setField("date_format", v)}>
                <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="dmy">DD/MM/YYYY</SelectItem>
                  <SelectItem value="mdy">MM/DD/YYYY</SelectItem>
                  <SelectItem value="ymd">YYYY-MM-DD</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Currency">
              <Input value={CURRENCY_LABELS[currency] || currency} readOnly className="h-9" />
            </Field>
            <Field label="Decimal Places">
              <Input
                value={form.decimal_places}
                onChange={(e) => setField("decimal_places", Number(e.target.value))}
                type="number"
                min={0}
                max={4}
                className="h-9"
              />
            </Field>
            <Field label="Week Starts On">
              <Select value={form.week_starts_on} onValueChange={(v) => setField("week_starts_on", v)}>
                <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="sat">Saturday</SelectItem>
                  <SelectItem value="sun">Sunday</SelectItem>
                  <SelectItem value="mon">Monday</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Shop Code">
              <Input
                value={form.shop_code}
                onChange={(e) => setField("shop_code", e.target.value)}
                className="h-9 font-mono"
                maxLength={2}
              />
            </Field>
          </div>
        </Card>

        <Card className="p-5">
          <SectionTitle>Security</SectionTitle>
          <Toggle label="Require login on idle (10 minutes)" checked={form.require_login_on_idle} onCheckedChange={(v) => setField("require_login_on_idle", v)} />
          <Toggle label="Enforce strong passwords" checked={form.enforce_strong_passwords} onCheckedChange={(v) => setField("enforce_strong_passwords", v)} />
          <Toggle label="Two-factor authentication for managers" checked={form.two_factor_managers} onCheckedChange={(v) => setField("two_factor_managers", v)} />
          <Toggle label="Audit log for every transaction" checked={form.audit_log_transactions} onCheckedChange={(v) => setField("audit_log_transactions", v)} />
          <Toggle label="Restrict counter access by IP" checked={form.restrict_counter_by_ip} onCheckedChange={(v) => setField("restrict_counter_by_ip", v)} />
          <Toggle label="Keyboard protection on POS" checked={form.keyboard_protection} onCheckedChange={(v) => setField("keyboard_protection", v)} />
        </Card>

        <Card className="p-5">
          <SectionTitle>System Preferences</SectionTitle>
          <Toggle label="Show keyboard shortcuts hint bar" checked={form.show_keyboard_shortcuts} onCheckedChange={(v) => setField("show_keyboard_shortcuts", v)} />
          <Toggle label="Auto-save form drafts" checked={form.auto_save_drafts} onCheckedChange={(v) => setField("auto_save_drafts", v)} />
          <Toggle label="Enable notification sounds" checked={form.notification_sounds} onCheckedChange={(v) => setField("notification_sounds", v)} />
          <Toggle label="Dark mode (Beta)" checked={form.dark_mode} onCheckedChange={(v) => setField("dark_mode", v)} />
          <Toggle label="Send anonymized usage analytics" checked={form.usage_analytics} onCheckedChange={(v) => setField("usage_analytics", v)} />
          <Toggle label="Show currency symbol on POS" checked={form.enable_currency_symbol} onCheckedChange={(v) => setField("enable_currency_symbol", v)} />
        </Card>

        <Card className="p-5">
          <SectionTitle>Backup & Data</SectionTitle>
          <Toggle label="Daily automatic backup at 02:00 AM" checked={form.daily_backup} onCheckedChange={(v) => setField("daily_backup", v)} />
          <Toggle label="Cloud backup (Emirates DC)" checked={form.cloud_backup} onCheckedChange={(v) => setField("cloud_backup", v)} />
          <Toggle label="Retain backups for 90 days" checked={form.retain_backups_90_days} onCheckedChange={(v) => setField("retain_backups_90_days", v)} />
          <div className="mt-3">
            <Button variant="outline" size="sm" className="h-8 text-[12px] w-full" onClick={() => toast.info("Backup download will be available in a future release")}>
              Download Full Backup
            </Button>
          </div>
        </Card>

        <Card className="p-5">
          <div className="flex items-center gap-2 mb-2">
            <Shield size={14} />
            <SectionTitle>Two-Factor Authentication</SectionTitle>
          </div>
          <p className="text-[11px] text-slate-500 mb-3">
            Required at login for owners and managers when enabled. Use Google Authenticator or similar.
          </p>
          {twoFaStatus?.enabled ? (
            <div className="space-y-2">
              <p className="text-[12px] text-emerald-700 font-medium">2FA is active on your account.</p>
              <Button
                variant="outline"
                size="sm"
                className="h-8"
                onClick={async () => {
                  await api.delete("/user/2fa");
                  queryClient.invalidateQueries({ queryKey: ["user-2fa"] });
                  toast.success("2FA disabled");
                }}
              >
                Disable 2FA
              </Button>
            </div>
          ) : twoFaSetup ? (
            <div className="space-y-2">
              <p className="text-[11px] text-slate-600">Add this secret to your app:</p>
              <code className="block text-[11px] bg-slate-100 p-2 rounded break-all">{twoFaSetup.secret}</code>
              <Input
                value={twoFaCode}
                onChange={(e) => setTwoFaCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                placeholder="6-digit code"
                className="h-9 font-mono"
              />
              <Button
                size="sm"
                className="h-8"
                onClick={async () => {
                  await api.post("/user/2fa/confirm", { code: twoFaCode });
                  setTwoFaSetup(null);
                  setTwoFaCode("");
                  queryClient.invalidateQueries({ queryKey: ["user-2fa"] });
                  toast.success("2FA enabled");
                }}
              >
                Confirm & enable
              </Button>
            </div>
          ) : (
            <Button
              size="sm"
              className="h-8"
              onClick={async () => {
                const { data: res } = await api.post("/user/2fa/setup");
                setTwoFaSetup(res);
              }}
            >
              Set up 2FA
            </Button>
          )}
        </Card>

        <Card className="p-5 xl:col-span-2">
          <SectionTitle>POS & Receipt (POSGeneralSetup)</SectionTitle>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8">
            <div>
              <Toggle label="Print bill on payment" checked={form.bill_print_on_payment} onCheckedChange={(v) => setField("bill_print_on_payment", v)} />
              <Toggle label="Show product rate on receipt" checked={form.show_product_rate_on_bill} onCheckedChange={(v) => setField("show_product_rate_on_bill", v)} />
              <Toggle
                label="Print barcode on receipt"
                hint="Shows the bill barcode at the bottom of POS receipts"
                checked={Boolean(form.barcode_print_enabled)}
                onCheckedChange={(v) => setField("barcode_print_enabled", v)}
              />
              <Toggle label="Receipt footer enabled" checked={form.receipt_footer_enabled} onCheckedChange={(v) => setField("receipt_footer_enabled", v)} />
              <Toggle
                label="Show logo on receipt"
                hint="Prefer Admin → Company Setup → Show logo on invoice. This toggle is a fallback."
                checked={Boolean(form.receipt_logo_enabled)}
                onCheckedChange={(v) => setField("receipt_logo_enabled", v)}
              />
              <Toggle label="Hold bill enabled" checked={form.hold_bill_enabled} onCheckedChange={(v) => setField("hold_bill_enabled", v)} />
              <Toggle label="Round bill total" checked={form.round_bill_total} onCheckedChange={(v) => setField("round_bill_total", v)} />
              <Toggle label="Open drawer on cash payment" checked={form.open_drawer_on_cash} onCheckedChange={(v) => setField("open_drawer_on_cash", v)} />
              <Toggle label="Enable delivery in POS" checked={form.enable_delivery_in_pos} onCheckedChange={(v) => setField("enable_delivery_in_pos", v)} />
              <Toggle label="Allow price change" checked={form.enable_price_change} onCheckedChange={(v) => setField("enable_price_change", v)} />
              <Toggle label="Enable promotion sales" checked={form.enable_promotion_sales} onCheckedChange={(v) => setField("enable_promotion_sales", v)} />
              <Toggle
                label="Show tips on POS"
                hint="Allow cashiers to enter optional tip amounts (TIP mode)"
                checked={form.enable_tips_on_pos !== false}
                onCheckedChange={(v) => setField("enable_tips_on_pos", v)}
              />
              <Toggle
                label="Show loyalty on POS"
                hint="Points balance, redeem, and usual order when a customer is on the bill"
                checked={form.enable_loyalty_on_pos !== false}
                onCheckedChange={(v) => setField("enable_loyalty_on_pos", v)}
              />
            </div>
            <div className="space-y-3">
              <p className="text-[12px] font-semibold text-slate-700">Receipt header (leave blank to use Company / Branch setup)</p>
              <Field label="Company name on receipt">
                <Input
                  value={form.receipt_header_company_name || ""}
                  onChange={(e) => setField("receipt_header_company_name", e.target.value)}
                  className="h-9"
                  placeholder="Leave blank to use company name"
                />
              </Field>
              <Field label="Phone on receipt">
                <Input
                  value={form.receipt_header_phone || ""}
                  onChange={(e) => setField("receipt_header_phone", e.target.value)}
                  className="h-9"
                  placeholder="Tel: +971 …"
                />
              </Field>
              <Field label="Address on receipt">
                <Input
                  value={form.receipt_header_address || ""}
                  onChange={(e) => setField("receipt_header_address", e.target.value)}
                  className="h-9"
                  placeholder="Street, area, city"
                />
              </Field>
              <Field label="TRN on receipt">
                <Input
                  value={form.receipt_header_trn || ""}
                  onChange={(e) => setField("receipt_header_trn", e.target.value)}
                  className="h-9 font-mono"
                  placeholder="Leave blank to use company TRN"
                />
              </Field>
              <Field label="Receipt Footer Line 1">
                <Input
                  value={form.receipt_footer_line1}
                  onChange={(e) => setField("receipt_footer_line1", e.target.value)}
                  className="h-9"
                  placeholder="Thank you visit again"
                />
                <p className="text-[11px] text-slate-500 mt-1">Printed on POS bills after the total. Leave blank to skip a line.</p>
              </Field>
              <Field label="Receipt Footer Line 2">
                <Input value={form.receipt_footer_line2} onChange={(e) => setField("receipt_footer_line2", e.target.value)} className="h-9" />
              </Field>
              <Field label="Receipt Footer Line 3">
                <Input value={form.receipt_footer_line3 || ""} onChange={(e) => setField("receipt_footer_line3", e.target.value)} className="h-9" />
              </Field>
              <Field label="Receipt Footer Line 4">
                <Input value={form.receipt_footer_line4 || ""} onChange={(e) => setField("receipt_footer_line4", e.target.value)} className="h-9" />
              </Field>
              <Field label="Max POS Discount %">
                <Input
                  type="number"
                  value={form.pos_discount_allowed_range}
                  onChange={(e) => setField("pos_discount_allowed_range", Number(e.target.value))}
                  className="h-9 text-right font-mono"
                  min={0}
                  max={100}
                />
              </Field>
            </div>
          </div>
        </Card>
      </div>
    </PageContainer>
  );
}
